
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Sidebar, 
  SidebarHeader, 
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar
} from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  FileText, 
  Lightbulb, 
  Users, 
  MessageSquare, 
  CircleHelp, 
  History,
  BarChart,
  MessageSquareText,
  BookMarked,
  Settings
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getCategories } from "@/services/categoryService";
import ProductTemplateSelector from "./ProductTemplateSelector";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { UserProfile } from "@/components/profile/types";

interface Category {
  id: string;
  name: string;
  icon: string;
  description?: string;
  color?: string;
}

interface ProductCopilotSidebarProps {
  onPromptSelect: (promptId: string) => void;
  selectedPrompt: string | null;
  activeCategory: string;
  setActiveCategory: (category: string) => void;
  onStartNewChat?: () => void;
}

const ProductCopilotSidebar = ({ 
  onPromptSelect, 
  selectedPrompt,
  activeCategory,
  setActiveCategory,
  onStartNewChat
}: ProductCopilotSidebarProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state } = useSidebar();
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  
  // Predefined main categories with more descriptive icons and colors
  const mainCategories: Category[] = [
    { id: 'prd', name: 'Product Requirements', icon: 'file-text', color: 'blue', description: 'Create PRDs, specs, and product documentation' },
    { id: 'brainstorm', name: 'Feature Brainstorming', icon: 'lightbulb', color: 'amber', description: 'Generate ideas and explore new features' },
    { id: 'research', name: 'Market Research', icon: 'bar-chart', color: 'orange', description: 'Analyze markets and competitors' },
    { id: 'voc', name: 'Customer Insights', icon: 'message-square', color: 'green', description: 'Understand user needs and feedback' },
  ];
  
  useEffect(() => {
    loadCategories();
  }, []);
  
  const loadCategories = async () => {
    try {
      const categoriesData = await getCategories();
      
      // Filter out categories that are already in mainCategories
      const otherCategories = categoriesData
        .filter(cat => !mainCategories.some(main => main.id === cat.id))
        .map(cat => ({
          id: cat.id,
          name: cat.name,
          icon: cat.icon || 'default',
          description: cat.description || `Templates for ${cat.name}`
        }));
      
      // Add other categories from the database
      setCategories(otherCategories);
    } catch (error) {
      console.error("Error loading categories:", error);
      toast({
        title: "Error",
        description: "Failed to load categories",
        variant: "destructive"
      });
    }
  };
  
  const getCategoryIcon = (iconType: string, color: string = '') => {
    const colorClasses: Record<string, string> = {
      'blue': 'text-blue-500',
      'amber': 'text-amber-500',
      'orange': 'text-orange-500',
      'green': 'text-green-500',
      'purple': 'text-purple-500',
      'yellow': 'text-yellow-500',
    };
    
    const colorClass = colorClasses[color] || '';
    
    switch(iconType) {
      case 'file-text':
        return <FileText className={`h-4 w-4 ${colorClass}`} />;
      case 'lightbulb':
        return <Lightbulb className={`h-4 w-4 ${colorClass}`} />;
      case 'bar-chart':
        return <BarChart className={`h-4 w-4 ${colorClass}`} />;
      case 'message-square':
        return <MessageSquare className={`h-4 w-4 ${colorClass}`} />;
      case 'users':
        return <Users className={`h-4 w-4 ${colorClass}`} />;
      case 'history':
        return <History className={`h-4 w-4 ${colorClass}`} />;
      case 'book-marked':
        return <BookMarked className={`h-4 w-4 ${colorClass}`} />;
      default:
        return <CircleHelp className={`h-4 w-4 ${colorClass}`} />;
    }
  };
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Navigate to search results with the query
    if (searchQuery.trim()) {
      navigate(`/product-copilot?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const getInitials = (name: string) => {
    if (!name) return "U";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  // Get user profile data safely
  const userProfile = user ? (user as any) : null;
  const userFirstName = userProfile?.first_name || userProfile?.user_metadata?.first_name || '';
  const userLastName = userProfile?.last_name || userProfile?.user_metadata?.last_name || '';
  const userAvatarUrl = userProfile?.avatar_url || userProfile?.user_metadata?.avatar_url || undefined;
  
  // Generate initials fallback
  const userInitials = user ? getInitials(userFirstName + " " + userLastName) : "U";
  
  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center justify-between py-2">
          <h3 className="text-lg font-semibold">Product Copilot</h3>
          
          {user && (
            <Avatar className="h-8 w-8">
              <AvatarImage src={userAvatarUrl} />
              <AvatarFallback>
                {userInitials}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
        <form onSubmit={handleSearch} className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search templates..."
            className="pl-8 w-full"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>
        <div className="mt-2 flex gap-2">
          <Button 
            variant="default" 
            className="flex-1"
            onClick={onStartNewChat}
          >
            <MessageSquareText className="mr-2 h-4 w-4" />
            New Chat
          </Button>
          
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => navigate('/chat-history')}
            title="Chat History"
          >
            <History className="h-4 w-4" />
          </Button>
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Categories</SidebarGroupLabel>
          <SidebarMenu>
            {mainCategories.map((category) => (
              <SidebarMenuItem key={category.id}>
                <SidebarMenuButton 
                  onClick={() => setActiveCategory(category.id)}
                  isActive={activeCategory === category.id}
                  title={category.description}
                >
                  {getCategoryIcon(category.icon, category.color)}
                  <span>{category.name}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
        
        {categories.length > 0 && (
          <SidebarGroup className="mt-4">
            <SidebarGroupLabel>Other Categories</SidebarGroupLabel>
            <ScrollArea className="h-40">
              <SidebarMenu>
                {categories.map((category) => (
                  <SidebarMenuItem key={category.id}>
                    <SidebarMenuButton 
                      onClick={() => setActiveCategory(category.id)}
                      isActive={activeCategory === category.id}
                      title={category.description}
                    >
                      {getCategoryIcon(category.icon)}
                      <span>{category.name}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </ScrollArea>
          </SidebarGroup>
        )}
        
        <SidebarGroup className="mt-4">
          <SidebarGroupLabel>Collections</SidebarGroupLabel>
          <SidebarMenu>
            {/* Removed Favorites SidebarMenuItem */}
            <SidebarMenuItem>
              <SidebarMenuButton 
                onClick={() => navigate('/chat-history')}
                isActive={activeCategory === "history"}
              >
                <History className="h-4 w-4" />
                <span>Recent Conversations</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton 
                onClick={() => navigate('/product-copilot')}
                isActive={false}
              >
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        
        <SidebarGroup className="mt-4">
          <SidebarGroupLabel>Recommended Templates</SidebarGroupLabel>
          <ScrollArea className="h-80">
            <div className="px-2 py-1">
              <ProductTemplateSelector
                onPromptSelect={onPromptSelect}
                selectedPrompt={selectedPrompt}
                filterType={activeCategory === "history" ? "recent" : "recent"}
                categoryFilter={activeCategory !== "history" ? activeCategory : undefined}
                maxItems={5}
                key={`selector-${activeCategory}`}
              />
            </div>
          </ScrollArea>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter>
        <div className="p-2">
          <Button variant="outline" className="w-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2"><circle cx="12" cy="12" r="10"/><path d="M12 8v8"/><path d="M8 12h8"/></svg>
            Create Template
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default ProductCopilotSidebar;
